import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { authApi } from "@/api";

declare const google: any;

export default function Login() {
  const [error, setError] = useState("");
  const [googleClientId, setGoogleClientId] = useState("");
  const [appName, setAppName] = useState("MA System");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      authApi.getGoogleClientId(),
      authApi.appConfig().catch(() => ({ appName: "MA System" })),
    ]).then(([{ clientId }, config]) => {
      if (cancelled) return;
      setAppName(config.appName || "MA System");
      document.title = config.appName || "MA System";
      setLoading(false);
      if (clientId) setGoogleClientId(clientId);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!googleClientId) return;

    const handleCredentialResponse = async (response: { credential: string }) => {
      setError("");
      try {
        await authApi.googleLogin(response.credential);
        navigate("/");
      } catch (err: any) {
        setError(err?.message || "Google login failed");
      }
    };

    const initializeGoogle = () => {
      google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
      });
      if (googleButtonRef.current) {
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          width: 256,
          text: "signin_with",
          locale: "ja",
        });
      }
    };

    if (typeof google !== "undefined" && google.accounts?.id) {
      initializeGoogle();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => initializeGoogle();
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [googleClientId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-80 space-y-4">
        <h1 className="text-xl font-bold text-center">{appName}</h1>
        <p className="text-sm text-slate-500 text-center">Googleアカウントでログイン</p>
        {error && (
          <p className="text-sm text-red-600 text-center">{error}</p>
        )}
        {loading ? (
          <p className="text-sm text-slate-400 text-center">読み込み中...</p>
        ) : googleClientId ? (
          <div ref={googleButtonRef} className="flex justify-center" />
        ) : (
          <p className="text-sm text-red-600 text-center">Google Client IDが設定されていません</p>
        )}
      </div>
    </div>
  );
}
